// The landing's ONLY script: the cookie notice (or, while the operator has the
// site's ad measurement switched on, the cookie choice), the demo-request POST,
// and the language a visitor lands in.
//
// It takes NO credential, and neither does the markup. The friend-code field
// that used to live here collected an invitation's secret on this host and
// handed it to the student panel in a URL - one origin taking a credential for
// another, which is what Google Safe Browsing classifies as phishing, and it
// flagged this domain for it. The code is typed on the panel that resolves it;
// this page only links there. Keep it that way: a name and an email for a demo
// request is the most this site may ask for.
//
// Every user-visible string lives in the documents and the shell's templates (the
// form's states are hidden elements in shell/demo.html that this file toggles), so
// the page stays static and the nostrings gate has nothing to see here.
//
// The panel links are NOT wired here any more — vite.config.ts bakes the real
// origins into the HTML at build time. A crawler (and a browser with a failed
// script) therefore reads the correct absolute links, which is the whole point:
// nothing on this page depends on JavaScript having run.
//
// Only the design system's FOUNDATION is imported — fonts, tokens, color-scheme.
// This page renders none of the shared primitives (it styles its own `.lp-*`
// markup), and pulling `styles.css` here shipped ~85 KB of render-blocking CSS
// the page never used.
import '@edtechcore/ui/foundation.css';
import './landing.css';
import { AD_WORDS, PARTNER_WORDS, carriedHref, wordsIn } from './carry';
import { languageToOpen } from './language';
import { CONSENT_KEY, fetchSettings, Measurement } from './measure';
import { followTrial } from './trial';
import { mailerFor, maskEmail } from './sent';
import { suggestEmail } from './typo';
import { workspaceDoor } from './join';
import { CHAT_KEY, startChat } from './chat';
import { startSheet } from './sheet';
import { emailProblem, type EmailProblem } from '@edtechcore/ui/emailCheck';

// --- The language a visitor lands in -------------------------------------------
// One URL per language is what makes the set indexable, and nothing here changes
// that: every version stays reachable at its own address, the `hreflang` set in
// each document names the others, and a crawler is never sent anywhere. This
// moves ONE thing - a person who arrives at the English home page with no
// language of their own on file, from a place whose clock or browser says they
// read one of the others.
//
// Which languages those are is read off the SWITCHER in the footer, not written
// out here: it is baked per language by the build, so a language can only ever
// be sent to an address this site actually publishes, and a fourth one needs no
// edit in this file.
//
// It fires on the home page only. A search result for the English privacy notice
// is a page somebody asked for by name, and answering it with a different
// language is the behaviour search engines ask sites not to have.
//
// The switcher always wins, and that has two halves. A recorded choice is never
// overridden - the escape hatch this needs to be fair, without which a Ukrainian
// reader who wants the English page could not keep it - and it is also HONOURED:
// somebody who picked Polish once and later types the bare domain arrives in
// Polish. A choice that only ever vetoed would be half a promise.
const LANG_KEY = 'etc-lang';

function storedLanguage(): string | null {
  try {
    return localStorage.getItem(LANG_KEY);
  } catch {
    return null; // private window: no memory, so no redirect either
  }
}

// Remember the language somebody picked, so the choice outlives the visit and
// the detection below never argues with it.
for (const link of document.querySelectorAll<HTMLAnchorElement>('.lp-langs a, .lp-lang__menu a')) {
  link.addEventListener('click', () => {
    try {
      localStorage.setItem(LANG_KEY, link.lang);
    } catch {
      // Nothing to remember it with. The link still works; only the memory is lost.
    }
  });
}

/** Every language this document offers, in the order the switcher names them. */
function offeredLanguages(): HTMLAnchorElement[] {
  return [...document.querySelectorAll<HTMLAnchorElement>('.lp-langs a')].filter((link) => link.lang);
}

/** The clock, when the browser has one to report. */
function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return ''; // No Intl: the rule falls through to the browser's language list.
  }
}

/** Whether this document is handing the visitor to another language's - then it measures nothing, and asks nothing. */
let leaving = false;

if (location.pathname === '/' || location.pathname === '/index.html') {
  // The rule itself lives in ./language.ts, which reads no DOM and is covered by
  // the landing's unit test; this is the page half - what to read it from, and
  // what to do with the answer. It runs on the English home page only, so no
  // other address is moved.
  const wanted = languageToOpen(
    offeredLanguages(),
    document.documentElement.lang,
    storedLanguage(),
    timeZone(),
    navigator.languages ?? [navigator.language],
  );
  if (wanted) {
    // `replace`, not `assign`: Back must return to wherever they came from, not to
    // the page that bounced them. The address's query and fragment travel with
    // them: an ad's link carries its click id there (`?gclid=`), and a partner's
    // carries their code - both are read on the page they land on, and a move
    // that dropped them would lose the click and the partner for every visitor
    // the move exists for.
    const target = new URL(wanted.href, location.href);
    target.search = location.search;
    target.hash = location.hash;
    leaving = true;
    location.replace(target.href);
  }
}

// --- The words an address carries on (src/carry.ts) -------------------------------
// The site's own links take a partner's code along on every page, and an ad's
// click only while the measurement question is up and unanswered: an Allow keeps
// the click itself (src/measure.ts), and a refusal means it is not wanted. Each
// link's own address is remembered, so taking the click back off restores it
// exactly.
const ownHref = new Map<HTMLAnchorElement, string>();

function carryOn(names: readonly string[]): void {
  const words = wordsIn(location.search, names);
  if (!words.length && !ownHref.size) return;
  for (const link of document.querySelectorAll<HTMLAnchorElement>('a[href]')) {
    let own = ownHref.get(link);
    if (own === undefined) {
      own = link.getAttribute('href') ?? '';
      ownHref.set(link, own);
    }
    link.setAttribute('href', carriedHref(own, location.href, words) ?? own);
  }
}

if (!leaving) carryOn(PARTNER_WORDS);

// --- The cookie notice, or the cookie choice --------------------------------------
// Which of the two a visitor sees is the operator's to say, in the console: the
// api hands the page the Google tag it may load (src/measure.ts).
//
// With no Google tag configured, the bar INFORMS rather than asks: the site then
// sets no advertising, analytics or tracking storage, so there is nothing to
// consent to and a Reject button would refuse something that does not exist
// (cookies.html says so in full). Its one entry records that it was read.
//
// With a tag configured, the bar ASKS, and nothing of Google's loads before the
// answer, anywhere (the server starts the tag refused; src/measure.ts says what
// the contract's other value, `granted`, would cost). The two answers are
// buttons of equal weight, the choice is remembered, and the footer's "Cookie
// settings" brings the question back, so taking an allowance back is as easy as
// giving it - and an answer given in another tab, or on a page this one was kept
// behind by Back, reaches this one too.
//
// Both bars are `hidden` in the markup and revealed here, so a browser running
// no JavaScript is shown neither - correctly, because without this script the
// page stores nothing and loads nothing.
const NOTICE_KEY = 'etc-cookie-notice';
const noticeBar = document.querySelector<HTMLElement>('#cookiebar');
const noticeOk = document.querySelector<HTMLButtonElement>('#cookiebar-ok');
const choiceBar = document.querySelector<HTMLElement>('#consentbar');
const choiceAllow = document.querySelector<HTMLButtonElement>('#consentbar-allow');
const choiceDecline = document.querySelector<HTMLButtonElement>('#consentbar-decline');
const choiceAgain = document.querySelector<HTMLButtonElement>('#cookie-settings');

// A page may carry no tag at all (`<body data-no-tag>`): the level test, which
// promises that nothing its reader answers leaves the browser.
const measurement = new Measurement(document, window, { tag: !document.body.hasAttribute('data-no-tag') });

/** Shows or hides a bar, keeping the footer clear of it while it is up. */
function showBar(bar: HTMLElement, shown: boolean): void {
  bar.hidden = !shown;
  // The bar is fixed to the bottom, so it sits ON the footer — including the
  // footer's own link to the policy. Reserving the space keeps every link on
  // the page reachable while it is up.
  document.body.classList.toggle(bar === choiceBar ? 'lp-has-choicebar' : 'lp-has-cookiebar', shown);
}

function showNotice(): void {
  if (!noticeBar || !noticeOk) return;
  // Storage can be unavailable (private windows, blocked site data). Then the
  // notice shows again next time — annoying, and still better than a page that
  // silently decides you have read something you have not.
  let acknowledged = false;
  try {
    acknowledged = localStorage.getItem(NOTICE_KEY) === '1';
  } catch {
    acknowledged = false;
  }
  if (acknowledged) return;
  showBar(noticeBar, true);
  noticeOk.addEventListener('click', () => {
    showBar(noticeBar, false);
    try {
      localStorage.setItem(NOTICE_KEY, '1');
    } catch {
      // Nothing to remember it with. The notice returns; nothing else breaks.
    }
  });
}

// Each answer to the cookie question adds one to the day's count of that answer
// on the server (POST /site-measurement/choices) - two numbers a day, nothing
// about who answered - so the owner knows what share of requests Google can see.
const countChoice = (choice: 'granted' | 'denied') => {
  try {
    void fetch('/api/v1/site-measurement/choices', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ choice }),
    }).catch(() => undefined);
  } catch {
    // Nothing to send it with: the answer stands on the device all the same.
  }
};

// The demo form's own copy of the question (shell/demo.html), shown only while
// the bar is waiting for an answer: ticking it and sending is the bar's Allow.
const adsRow = document.querySelector<HTMLElement>('#demo-ads-row');
const adsBox = document.querySelector<HTMLInputElement>('#demo-ads');
/** The bar's answer, for the form's box to give: set where the bar is wired. */
let answerQuestion: ((choice: 'granted' | 'denied') => void) | null = null;

if (choiceBar && choiceAllow && choiceDecline) {
  // Whoever opened the bar from the footer gets their place back when they
  // answer; a visitor answering the bar that greeted them stays where they are.
  let reopened = false;
  const answer = (choice: 'granted' | 'denied') => {
    measurement.choose(choice);
    countChoice(choice);
    carryOn(PARTNER_WORDS);
    showBar(choiceBar, false);
    if (adsRow) adsRow.hidden = true;
    if (reopened) choiceAgain?.focus();
    reopened = false;
  };
  answerQuestion = answer;
  choiceAllow.addEventListener('click', () => answer('granted'));
  choiceDecline.addEventListener('click', () => answer('denied'));
  choiceAgain?.addEventListener('click', () => {
    reopened = true;
    showBar(choiceBar, true);
    // The bar itself takes focus, not either answer: Enter must not mean "allow"
    // to somebody who came to take an allowance back.
    choiceBar.focus();
  });
  // Another tab answered, or Back restored this page from the cache after an
  // answer on a later one: catch up, and put the question away if it is settled.
  const catchUp = () => {
    if (!measurement.active) return;
    if (!measurement.sync() && !choiceBar.hidden && !reopened) {
      showBar(choiceBar, false);
      carryOn(PARTNER_WORDS);
    }
  };
  window.addEventListener('storage', (e) => {
    if (e.key === CONSENT_KEY) catchUp();
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) catchUp();
  });
}

if (!leaving) {
  void fetchSettings().then((settings) => {
    const mustAsk = measurement.begin(settings);
    if (!measurement.active || !choiceBar) {
      // An api that could not be asked tells a visitor who has answered the
      // question nothing new, and the plain notice would deny the cookies their
      // allowance left. The page loads nothing either way.
      if (settings === undefined && measurement.storedChoice() !== null) return;
      showNotice();
      return;
    }
    if (choiceAgain) choiceAgain.hidden = false;
    if (mustAsk) {
      showBar(choiceBar, true);
      carryOn([...PARTNER_WORDS, ...AD_WORDS]);
      if (adsRow) adsRow.hidden = false;
    }
  });
}

// --- The demo-request form ----------------------------------------------------
// The one API write on the site, proxied same-origin (/api → backend; the prod
// edge routes the apex's /api the same way). Success appears only after the
// API says so; a failure says so too — nothing is pretended.
//
// A document carries one form or none: the pages that sell carry it, written in
// by the build from shell/demo.html, and every other page links to the home
// page's. So the ids below find it wherever it is, and a page without it binds
// nothing.
/** The partner's code in this page's address (`?ref=CODE`), as the request carries it. */
const partnerRef = (): { ref?: string } => {
  const ref = new URLSearchParams(window.location.search).get('ref')?.trim().toUpperCase() ?? '';
  return /^[A-Z2-7]{1,16}$/.test(ref) ? { ref } : {};
};

const demoForm = document.querySelector<HTMLFormElement>('#demo-form');
const demoDone = document.querySelector<HTMLElement>('#demo-done');
const emailError = document.querySelector<HTMLElement>('#demo-email-error');
const consentBox = document.querySelector<HTMLInputElement>('#demo-consent');
const consentError = document.querySelector<HTMLElement>('#demo-consent-error');
const domainError = document.querySelector<HTMLElement>('#demo-domain-error');
const sendError = document.querySelector<HTMLElement>('#demo-send-error');
const submitBtn = document.querySelector<HTMLButtonElement>('#demo-submit');

if (demoForm && demoDone && emailError && domainError && sendError && submitBtn && consentBox && consentError) {
  // What was sent, held for "send it again" and "change the address": the form
  // itself is emptied the moment the request is recorded (below).
  let sent: { name: string; email: string; audience: 'teacher' | 'school' } | null = null;
  const heardSelect = demoForm.querySelector<HTMLSelectElement>('#demo-heard');
  const heardFrom = (): { heardFrom?: string } => {
    const v = heardSelect?.value ?? '';
    return ['google', 'threads', 'instagram', 'colleagues', 'other'].includes(v) ? { heardFrom: v } : {};
  };
  const usedChat = (): boolean => {
    try {
      return localStorage.getItem(CHAT_KEY) !== null;
    } catch {
      return false;
    }
  };
  const send = (body: { name: string; email: string; audience: 'teacher' | 'school' }) =>
    fetch('/api/v1/demo-requests', {
      method: 'POST',
      // This document knows its own language - it was built per locale - and the
      // visitor has no account to hold one. The header is what the server reads
      // to decide which language to write the confirmation email in, so a
      // Polish page's request must ask in Polish even when the browser would not.
      headers: { 'Content-Type': 'application/json', 'Accept-Language': document.documentElement.lang || 'en' },
      // `consent` is only ever sent past the check on the form. The server
      // refuses a request without it and records the moment the request arrived,
      // so the tick is something the platform can show, not only a promise.
      // `ref` is a partner's share code, read from this page's own address at the
      // moment of sending and from nowhere else: no field, no cookie, no storage,
      // so the notices that promise their absence stay true. Absent when the
      // address carries none; the server drops a code nobody has.
      //
      // `attribution` is the ad click the visit came from, kept since the click
      // in a cookie - and only for a visitor who allowed the site's measurement,
      // with the version of the question they allowed, which the server keeps it
      // under (src/measure.ts). Absent otherwise.
      //
      // `test` says the visit is the people who run the site checking it
      // (`?etc_test=1`, or a TEST click id): the request is answered like any
      // other and kept out of the ad network's file. Sent without an Allow too -
      // it says nothing about the visitor.
      //
      // `page`, `heardFrom` and `fromChat` are where the request came from,
      // told without a cookie: this page's path (never its query - a click id
      // would ride along), the visitor's own answer to "how did you hear about
      // us" if they gave one, and whether this browser holds a conversation of
      // the site's chat - a yes or no, never which one.
      body: JSON.stringify({
        ...body,
        consent: true,
        ...partnerRef(),
        attribution: measurement.attributionForRequest(),
        ...(measurement.testVisit() ? { test: true } : {}),
        page: window.location.pathname,
        ...heardFrom(),
        fromChat: usedChat(),
      }),
    });

  // The one step left, in place of the form: where the letter went (masked), the
  // inbox to open it in, and a school told what this workspace is. Scrolled to
  // and focused, so on a phone it is not left under the nav while the page still
  // shows the bottom of a tall form.
  const resent = demoDone.querySelector<HTMLElement>('#demo-resent');
  const resendError = demoDone.querySelector<HTMLElement>('#demo-resend-error');
  const showDone = (email: string, audience: 'teacher' | 'school') => {
    const addr = demoDone.querySelector<HTMLElement>('.lp-demo-done__addr');
    if (addr) addr.textContent = maskEmail(email);
    const school = demoDone.querySelector<HTMLElement>('.lp-demo-done__school');
    if (school) school.hidden = audience !== 'school';
    const mail = demoDone.querySelector<HTMLAnchorElement>('#demo-done-mail');
    const mailer = mailerFor(email);
    if (mail) {
      mail.hidden = !mailer;
      if (mailer) {
        mail.href = mailer.href;
        const name = mail.querySelector<HTMLElement>('.lp-demo-done__mailer');
        if (name) name.textContent = mailer.name;
      }
    }
    if (resent) resent.hidden = true;
    if (resendError) resendError.hidden = true;
    demoForm.hidden = true;
    demoDone.hidden = false;
    demoDone.scrollIntoView({ block: 'start', behavior: 'auto' });
    demoDone.querySelector<HTMLElement>('#demo-done-title')?.focus({ preventScroll: true });
  };

  // Again: the same request sent again, which the server answers with the same
  // link (a second request never revokes the first letter's). Not a conversion -
  // the request was counted when it was recorded.
  const resendBtn = demoDone.querySelector<HTMLButtonElement>('#demo-resend');
  resendBtn?.addEventListener('click', () => {
    if (!sent) return;
    resendBtn.disabled = true;
    if (resent) resent.hidden = true;
    if (resendError) resendError.hidden = true;
    void send(sent)
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        if (resent) resent.hidden = false;
      })
      .catch(() => {
        if (resendError) resendError.hidden = false;
      })
      .finally(() => {
        resendBtn.disabled = false;
      });
  });
  // Another address: the form again, with the name and the choice kept.
  demoDone.querySelector<HTMLButtonElement>('#demo-change')?.addEventListener('click', () => {
    demoDone.hidden = true;
    demoForm.hidden = false;
    if (sent) {
      const nameInput = demoForm.querySelector<HTMLInputElement>('input[name="name"]');
      if (nameInput) nameInput.value = sent.name;
      const choice = demoForm.querySelector<HTMLInputElement>(`input[name="audience"][value="${sent.audience}"]`);
      if (choice) choice.checked = true;
    }
    demoForm.querySelector<HTMLInputElement>('input[name="email"]')?.focus();
  });

  const emailInput = demoForm.querySelector<HTMLInputElement>('input[name="email"]');
  const showEmailProblem = (problem: EmailProblem | null) => {
    emailError.hidden = problem === null;
    // The field says it is wrong and which line says why (read by screen readers).
    if (problem === null) emailInput?.removeAttribute('aria-invalid');
    else emailInput?.setAttribute('aria-invalid', 'true');
    for (const line of emailError.querySelectorAll<HTMLElement>('[data-email-problem]')) {
      line.hidden = line.dataset.emailProblem !== problem;
    }
  };
  // A mail provider's name mistyped (src/typo.ts) is offered on leaving the
  // field, and the first press of the button with it unanswered stops to show
  // it - a line that appeared while the eye was on the consent box is easy to
  // miss. Never forced: pressed again, the address goes as typed. The letter
  // with the workspace link goes to it, so a typo there is a request nobody can
  // answer.
  const suggestLine = demoForm.querySelector<HTMLElement>('#demo-email-suggest');
  const suggestAddr = suggestLine?.querySelector<HTMLElement>('.lp-email-suggest__addr') ?? null;
  let stoppedFor: string | null = null;
  /** Shows the fix for this address, or hides the line when there is none; true when there is one. */
  const offerFix = (email: string): boolean => {
    const better = suggestEmail(email);
    if (!suggestLine || !suggestAddr || better === null) {
      if (suggestLine) suggestLine.hidden = true;
      return false;
    }
    suggestAddr.textContent = better;
    suggestLine.hidden = false;
    return true;
  };
  /** The press stops, once per address, while a fix is on offer. */
  const stopForFix = (email: string): boolean => {
    if (!offerFix(email) || stoppedFor === email) return false;
    stoppedFor = email;
    return true;
  };
  emailInput?.addEventListener('blur', () => {
    const email = emailInput.value.trim();
    if (emailProblem(email) === null) offerFix(email);
  });
  emailInput?.addEventListener('input', () => {
    if (suggestLine) suggestLine.hidden = true;
  });
  demoForm.querySelector<HTMLButtonElement>('#demo-email-suggest-fix')?.addEventListener('click', () => {
    const better = suggestAddr?.textContent;
    if (!emailInput || !better || !suggestLine) return;
    emailInput.value = better;
    suggestLine.hidden = true;
    emailInput.focus();
  });
  let reported = false;
  // Set once the answer opens the workspace and the page is on its way there.
  let leaving = false;
  const opening = demoForm.querySelector<HTMLElement>('#demo-opening');
  // Back from the workspace may bring this page out of the browser's page cache
  // just as it was left - the button pressed, "Opening your workspace…" shown.
  // It is a form again: the request was recorded, and another is a new request.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    leaving = false;
    submitBtn.disabled = false;
    if (opening) opening.hidden = true;
  });
  demoForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const data = new FormData(demoForm);
    const name = String(data.get('name') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    // Which demo. The build ticks one of the two on every page that carries the
    // form - the page's own reader's, or `teacher` where the page is written for
    // both (src/shell.ts) - so this is only ever one of the two the API accepts;
    // anything else means the DOM was edited, and the server settles that back to
    // `teacher` rather than refusing a lead.
    const audience = data.get('audience') === 'school' ? 'school' : 'teacher';
    // Consent is a condition of sending, not a formality: without the tick the
    // request never leaves the page. Checked BEFORE the address, so a visitor who
    // has done neither is told about both in one pass rather than one per attempt.
    const consented = consentBox.checked;
    consentError.hidden = consented;
    // The address is held to the server's own rule, and its problem is named -
    // a space, the keyboard left in Cyrillic, a missing dot after @ - instead of
    // "could not send" once the server refuses what the form let through.
    const problem = emailProblem(email);
    showEmailProblem(problem);
    if (!consented || problem) return;
    if (stopForFix(email)) return;
    domainError.hidden = true;
    sendError.hidden = true;
    submitBtn.disabled = true;
    // The form's copy of the cookie question, ticked: the bar's Allow, given
    // before the request leaves, so the request carries the click it allows and
    // the tag starts loading while the request is on its way.
    const allowedHere = !!(adsRow && !adsRow.hidden && adsBox?.checked);
    if (allowedHere) {
      if (answerQuestion) answerQuestion('granted');
      else measurement.choose('granted');
    }
    void send({ name, email, audience })
      .then(async (res) => {
        // One refusal earns its own sentence: the platform does not register
        // accounts from some mail providers, and the address the visitor typed
        // is otherwise perfectly good. "Please try again" would be a lie - the
        // same address will be refused every time.
        if (res.status === 400) {
          const body = (await res.json().catch(() => null)) as { error?: { code?: string } } | null;
          if (body?.error?.code === 'leads.email_domain_blocked') {
            domainError.hidden = false;
            return;
          }
          // The address itself was refused - by the contract's decoder
          // (`invalid_request`) or the handler (`leads.invalid_request`): say so
          // at the address, and take the person back to it.
          if (body?.error?.code === 'invalid_request' || body?.error?.code === 'leads.invalid_request') {
            showEmailProblem('shape');
            emailInput?.focus();
            return;
          }
        }
        if (!res.ok) throw new Error(String(res.status));
        sent = { name, email, audience };
        const receipt = (await res.json().catch(() => null)) as { id?: unknown; joinUrl?: unknown } | null;
        // The workspace is open to this browser: the answer names its join page
        // (a handle of this request's own in the fragment), and the letter with
        // the login goes to the address as well. Only the teacher panel this
        // build names is followed (src/join.ts); anything else is the letter's
        // way in, as before.
        const door = workspaceDoor(receipt?.joinUrl, demoForm.dataset.joinOrigin, window.location.protocol);
        if (door) {
          leaving = true;
          if (opening) opening.hidden = false;
        } else {
          showDone(email, audience);
        }
        // Emptied before anything is reported: Google's tag can be set, in
        // Google's own consoles, to look for an address on the page, and the
        // notices promise that none reaches it (docs/OPEN_WORK.md).
        demoForm.reset();
        // The conversion is the RECORDED request, so it is reported here and
        // nowhere else - never on the press, never on a refusal, and never on a
        // load, so a reload or Back reports nothing. The server's id is its
        // transaction id: Google counts one request once.
        // Once per page, too: "Change it" and a corrected address is the same
        // person's one request, not a second conversion.
        // Going to the workspace, the page leaves only once the report is on
        // its way (or after a second, whatever the tag does) - a hit queued on a
        // page that unloads is a conversion nobody counts. The server's
        // registrations file is the ad account's backstop either way.
        const go = door ? () => window.location.assign(door) : undefined;
        if (typeof receipt?.id === 'string' && !reported) {
          reported = true;
          // A tag the form's box only just started has its script still on the
          // way: it gets longer before the page leaves.
          measurement.reportSignup({ id: receipt.id, audience }, go ? { then: go, within: allowedHere ? 2500 : 1000 } : undefined);
        } else {
          go?.();
        }
      })
      .catch(() => {
        sendError.hidden = false;
      })
      .finally(() => {
        // Leaving for the workspace: the button stays pressed, so a second
        // press cannot file the request twice on the way out.
        submitBtn.disabled = leaving;
      });
  });
}

startSheet();

// The chat's first question, as one of the page's moments (src/chat.ts): only
// through a tag the visitor allowed, and never what was asked.
window.addEventListener('lp:chat-start', () => {
  measurement.reportMoment({ name: 'chat_start', page_path: window.location.pathname });
});

// On a phone, the cookie bar steps aside while the visitor types in the request
// form, so it never covers a field or the button; it comes back when they leave
// the form (landing.css, .lp-typing).
if (demoForm) {
  demoForm.addEventListener('focusin', () => document.body.classList.add('lp-typing'));
  demoForm.addEventListener('focusout', (e) => {
    if (!demoForm.contains(e.relatedTarget as Node | null)) document.body.classList.remove('lp-typing');
  });
}

// --- The page's moments ---------------------------------------------------------
// What the analytics side hears of beside a recorded request (src/measure.ts,
// `Moment`), and only through a tag the visitor allowed: a press on any "Try it
// free" - every one leads to a form, so it is told by its #demo address - and
// where on the page it stood; the first touch of the form; a press on Telegram or
// the phone. Nothing typed is read.
const ctaLocation = (el: Element): 'hero' | 'header' | 'section' | 'footer' =>
  el.closest('.lp-nav, .lp-header') ? 'header' : el.closest('footer') ? 'footer' : el.closest('.au-hero, .lp-hero, .tr-hero, .hm-hero') ? 'hero' : 'section';
document.addEventListener(
  'click',
  (e) => {
    const link = (e.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
    if (!link) return;
    const href = link.getAttribute('href') ?? '';
    const page_path = window.location.pathname;
    if (/#demo$/.test(href)) measurement.reportMoment({ name: 'cta_click', cta_location: ctaLocation(link), page_path });
    else if (href.startsWith('https://t.me/')) measurement.reportMoment({ name: 'contact_click', channel: 'telegram', page_path });
  },
  { capture: true },
);
// The phone is pressed where it is opened (the footer's disclosure): that press is the moment.
for (const phone of document.querySelectorAll<HTMLDetailsElement>('details.lp-footer-phone')) {
  phone.addEventListener('toggle', () => {
    if (phone.open) measurement.reportMoment({ name: 'contact_click', channel: 'phone', page_path: window.location.pathname });
  });
}
const formForMoments = document.querySelector<HTMLFormElement>('#demo-form');
formForMoments?.addEventListener(
  'focusin',
  () => {
    const role = formForMoments.querySelector<HTMLInputElement>('input[name="audience"]:checked')?.value === 'school' ? 'school' : 'teacher';
    measurement.reportMoment({ name: 'form_start', user_role: role, page_path: window.location.pathname });
  },
  { once: true },
);

// --- The header's menus -------------------------------------------------------
// Progressive enhancement, nothing more. The sign-in and language menus are
// <details> that open and close on their own summaries with no script at all, and
// the phone menu is a native popover the browser opens, closes on Escape and
// closes on a tap outside. A browser that never runs this file loses only what
// follows: a click away from a <details> menu, or Escape, closes it; and a link
// inside the phone menu closes the menu - which matters for the one link that
// stays on the page it is on: the demo form's, on every page that carries it.
// It reads no input and sends nothing.
const menus = [...document.querySelectorAll<HTMLDetailsElement>('.lp-signin, .lp-lang')];
if (menus.length) {
  document.addEventListener('click', (e) => {
    for (const menu of menus) {
      if (menu.open && !menu.contains(e.target as Node)) menu.open = false;
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    for (const menu of menus) {
      if (!menu.open) continue;
      menu.open = false;
      menu.querySelector('summary')?.focus();
    }
  });
}

const siteMenu = document.querySelector<HTMLElement>('#lp-menu');
if (siteMenu && typeof siteMenu.hidePopover === 'function') {
  siteMenu.addEventListener('click', (e) => {
    if (!(e.target as Element).closest('a')) return;
    try {
      if (siteMenu.matches(':popover-open')) siteMenu.hidePopover();
    } catch {
      // A browser that knows hidePopover but not the pseudo-class: nothing to close.
    }
  });
}

// The trial's length is a dial in the console. The document already says it - the
// number was baked at build - and this only asks whether the dial has moved since,
// re-saying the sentences that state it if so (src/trial.ts). A page that states
// no trial asks nothing.
void followTrial();
startChat();
